import React from 'react';
import { motion } from 'framer-motion';
import { Zap, BookOpen, Microscope, Dna, Leaf, Heart, Globe, Trophy, FlaskConical } from 'lucide-react';
const volumes = [{
  year: 'N1',
  color: 'bg-blue-500'
}, {
  year: 'N2',
  color: 'bg-green-500'
}, {
  year: 'N3',
  color: 'bg-orange-500'
}, {
  year: 'N4',
  color: 'bg-purple-500'
}, {
  year: 'N5',
  color: 'bg-slate-500'
}, {
  year: 'N6',
  color: 'bg-red-500'
}, {
  year: 'N7',
  color: 'bg-yellow-500',
  isSpecial: true
}];
const details = [{
  title: 'Tomo 1 — Nivel Inicial: La Vida, su Organización y Diversidad',
  desc: 'Célula, nutrición y ecología básica.',
  icon: <Microscope className="text-blue-600" data-id="element-185" />
}, {
  title: 'Tomo 2 — Nivel Inicial e Intermedio: Evolución y Origen de la Vida',
  desc: 'Evolución, genética, origen de la vida y biotecnología.',
  icon: <Leaf className="text-green-600" data-id="element-186" />
}, {
  title: 'Tomo 3 — Nivel Intermedio: El Cuerpo Humano como Sistema Complejo',
  desc: 'Sistemas del cuerpo humano, salud, prevención y educación integral.',
  icon: <Heart className="text-orange-600" data-id="element-187" />
}, {
  title: 'Tomo 4 — Nivel Intermedio y Avanzado: Genética Molecular, Biotecnología y Metabolismo',
  desc: 'ADN, duplicación, PCR y CRISPR.',
  icon: <Dna className="text-purple-600" data-id="element-188" />
}, {
  title: 'Tomo 5 — Nivel Avanzado: Biodiversidad, Ecosistemas y Problemáticas Ambientales',
  desc: 'Estudios de ecosistemas, biodiversidad y problemáticas ambientales locales y globales.',
  icon: <Globe className="text-slate-600" data-id="element-189" />
}, {
  title: 'Tomo 6 — Nivel de Profundización: Evolución Humana, Salud Pública y Bioética',
  desc: 'Evolución humana, salud pública, bioética y análisis de debates científicos.',
  icon: <FlaskConical className="text-red-600" data-id="element-190" />
}, {
  title: 'Tomo 7 — PROYECTOS INTERDISCIPLINARIOS Y FERIA DE CIENCIAS',
  desc: '15 proyectos interdisciplinarios de ciencias y tecnología desarrollados paso a paso: sistemas de poleas, hidráulica, energía solar, energía eólica, motor eléctrico, cohete de agua y vivienda sustentable. Incluye una guía para presentar cada proyecto ante el jurado.',
  icon: <Trophy className="text-yellow-600" data-id="element-191" />
}];
export function ProductBreakdown() {
  return <section className="py-8 md:py-20 px-4 max-w-4xl mx-auto" data-id="element-192">
      <div className="text-center mb-6 md:mb-16" data-id="element-193">
        <div className="inline-flex items-center gap-2 text-emerald-600 font-black tracking-widest text-sm uppercase mb-4" data-id="element-194">
          <Dna size={18} className="fill-emerald-600" data-id="element-195" />
          <span data-id="element-196">Biología en Acción</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 leading-tight" data-id="element-197">
          MEGAPACK BIOLOGÍA EN ACCIÓN
        </h2>
        <p className="text-slate-600 font-bold text-sm md:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-198">
          7 tomos organizados por niveles. Más de 360 páginas con actividades
          secuenciadas, prácticas de laboratorio y evaluaciones. Contenidos
          fundamentales de Biología adaptables al programa educativo de cada
          institución.
        </p>
      </div>

      {/* Visual Volumes Grid */}
      <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4 mb-8 md:mb-20" data-id="element-199">
        {volumes.map((v, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className={`${v.color} aspect-[3/4] rounded-lg md:rounded-xl flex flex-col items-center justify-center text-white shadow-lg relative overflow-hidden group`} data-id="element-200">
            <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-201" />
            <span className={`font-black ${v.isSpecial ? 'text-[10px] md:text-xs' : 'text-xl md:text-2xl'}`} data-id="element-202">
              {v.year}
            </span>
            {v.isSpecial && <Trophy size={14} className="mt-1" data-id="element-203" />}
          </motion.div>)}
      </div>

      <div className="text-center mb-8 md:mb-12" data-id="element-204">
        <h3 className="text-2xl md:text-4xl font-black text-slate-900" data-id="element-205">
          Todo lo que <span className="italic text-emerald-600" data-id="element-206">recibes</span>
        </h3>
      </div>

      {/* Details List */}
      <div className="space-y-4" data-id="element-207">
        {details.map((item, i) => <motion.div key={i} initial={{
        opacity: 0,
        x: -10
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.1
      }} className="bg-white p-5 md:p-6 rounded-2xl border border-emerald-50 shadow-sm flex gap-4 items-start" data-id="element-208">
            <div className="bg-slate-50 p-3 rounded-xl shrink-0" data-id="element-209">
              {item.icon}
            </div>
            <div data-id="element-210">
              <h4 className="font-black text-slate-900 text-sm md:text-lg mb-1 leading-tight" data-id="element-211">
                {item.title}
              </h4>
              <p className="text-slate-600 text-xs md:text-base leading-relaxed font-medium" data-id="element-212">
                {item.desc}
              </p>
            </div>
          </motion.div>)}
      </div>
    </section>;
}